// src/components/ui/PhotoUpload.js - PRODUCTION VERSION with Structured S3 Keys & Persistent Cache
import { useEffect, useRef, useState } from 'react';

const PhotoUpload = ({
      photos = [],
      onPhotosChange,
      referenceNo,
      sectionName,
      disabled = false,
      maxPhotos = 5,
      itemIndex = 1 // New prop to track which item this is for structured naming
}) => {
      const [uploading, setUploading] = useState(false);
      const [uploadError, setUploadError] = useState(null);
      const [retryQueue, setRetryQueue] = useState([]);
      const [autoRetrying, setAutoRetrying] = useState(false);
      const fileInputRef = useRef(null);

      // Initialize from cache on component mount
      useEffect(() => {
            loadFromCache();
            // Start auto-retry process for failed uploads
            const interval = setInterval(processRetryQueue, 10000); // Retry every 10 seconds
            return () => clearInterval(interval);
      }, [referenceNo, sectionName]);

      // Generate structured S3 key for document integration
      const generateStructuredS3Key = (photoIndex, fileExtension) => {
            // Format: referenceNo/sectionName/sectionName_item{itemIndex}_photo{photoIndex}.extension
            // Example: FOR243.2A-EFS-2025-01-15-123456-7/cleaning-maintenance/cleaning-maintenance_item1_photo1.jpg
            return `${referenceNo}/${sectionName}/${sectionName}_item${itemIndex}_photo${photoIndex + 1}.${fileExtension}`;
      };

      // Save photos to persistent cache (with size limit protection)
      const saveToCache = (photosArray) => {
            try {
                  const cacheKey = `photos_${referenceNo}_${sectionName}_${itemIndex}`;
                  const cacheData = {
                        photos: photosArray.map(photo => ({
                              // Only store essential metadata, not large data
                              id: photo.id,
                              filename: photo.filename,
                              s3Key: photo.s3Key,
                              s3Url: photo.s3Url,
                              uploadedAt: photo.uploadedAt,
                              fileSize: photo.fileSize,
                              fileType: photo.fileType,
                              fileExtension: photo.fileExtension,
                              uploadStatus: photo.uploadStatus,
                              // Don't store large data like apiResponse or blob URLs
                        })),
                        lastUpdated: new Date().toISOString(),
                        referenceNo,
                        sectionName,
                        itemIndex
                  };

                  // Check size before storing
                  const dataSize = JSON.stringify(cacheData).length;
                  if (dataSize > 1000000) { // 1MB limit
                        console.warn('Cache data too large, skipping localStorage save');
                        return;
                  }

                  localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            } catch (error) {
                  console.error('Failed to save photos to cache:', error);
                  // Clear some cache if storage is full
                  try {
                        const keys = Object.keys(localStorage);
                        const photoKeys = keys.filter(key => key.startsWith('photos_') || key.startsWith('retry_'));
                        // Remove oldest cache entries
                        photoKeys.slice(0, Math.min(5, photoKeys.length)).forEach(key => {
                              localStorage.removeItem(key);
                        });
                  } catch (clearError) {
                        console.error('Failed to clear cache:', clearError);
                  }
            }
      };

      // Load photos from persistent cache (no retry queue loading)
      const loadFromCache = () => {
            try {
                  const cacheKey = `photos_${referenceNo}_${sectionName}_${itemIndex}`;
                  const cachedData = localStorage.getItem(cacheKey);

                  if (cachedData) {
                        const parsed = JSON.parse(cachedData);
                        if (parsed.photos && parsed.photos.length > 0) {
                              onPhotosChange(parsed.photos);
                        }
                  }

                  // Don't load retry queue from localStorage since we keep it in memory only
            } catch (error) {
                  console.error('Failed to load photos from cache:', error);
            }
      };

      // Add to retry queue for failed uploads (store file reference, not data)
      const addToRetryQueue = (file, photoData, retryCount = 0) => {
            const retryItem = {
                  id: Date.now() + Math.random(),
                  file: file, // Store the actual File object in memory
                  fileName: file.name,
                  fileSize: file.size,
                  fileType: file.type,
                  photoData,
                  retryCount,
                  maxRetries: 2,
                  addedAt: new Date().toISOString(),
                  lastAttempt: null
            };

            setRetryQueue(prev => [...prev, retryItem]);
            // Don't store retry queue in localStorage to avoid quota issues
            // Keep it in component state only
      };

      // Process retry queue automatically
      const processRetryQueue = async () => {
            if (retryQueue.length === 0 || autoRetrying || uploading) return;

            setAutoRetrying(true);
            const failedItems = retryQueue.filter(item => item.retryCount < item.maxRetries);

            if (failedItems.length === 0) {
                  setAutoRetrying(false);
                  return;
            }

            console.log(`Auto-retrying ${failedItems.length} failed photo uploads...`);

            for (const item of failedItems.slice(0, 2)) { // Retry max 2 at a time
                  try {
                        const success = await uploadSinglePhoto(item.file, item.photoData, true);

                        if (success) {
                              // Remove from retry queue
                              setRetryQueue(prev => prev.filter(qItem => qItem.id !== item.id));
                        } else {
                              // Increment retry count
                              setRetryQueue(prev => prev.map(qItem =>
                                    qItem.id === item.id
                                          ? { ...qItem, retryCount: qItem.retryCount + 1, lastAttempt: new Date().toISOString() }
                                          : qItem
                              ));
                        }
                  } catch (error) {
                        console.error('Retry upload failed:', error);
                        // Increment retry count on error
                        setRetryQueue(prev => prev.map(qItem =>
                              qItem.id === item.id
                                    ? { ...qItem, retryCount: qItem.retryCount + 1, lastAttempt: new Date().toISOString() }
                                    : qItem
                        ));
                  }
            }

            setAutoRetrying(false);
      };

      // Manual retry for specific failed upload
      const manualRetry = async (retryItemId) => {
            const item = retryQueue.find(q => q.id === retryItemId);
            if (!item || !item.file) return;

            try {
                  const success = await uploadSinglePhoto(item.file, item.photoData, true);

                  if (success) {
                        setRetryQueue(prev => prev.filter(qItem => qItem.id !== retryItemId));
                  } else {
                        // Increment retry count
                        setRetryQueue(prev => prev.map(qItem =>
                              qItem.id === retryItemId
                                    ? { ...qItem, retryCount: qItem.retryCount + 1, lastAttempt: new Date().toISOString() }
                                    : qItem
                        ));
                  }
            } catch (error) {
                  console.error('Manual retry failed:', error);
            }
      };

      // Upload single photo using presigned PUT (no base64 fallback)
      // The backend now rejects non-signature base64 uploads, so we must always perform direct S3 PUT via presigned URL.
      const uploadSinglePhoto = async (file, photoDataOverride = null, isRetry = false) => {
            let s3Key;

            try {
                  console.log(`Starting presigned upload for file: ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);

                  // Map MIME types to proper extensions
                  let fileExtension;
                  switch (file.type.toLowerCase()) {
                        case 'image/jpeg':
                        case 'image/jpg':
                              fileExtension = 'jpg';
                              break;
                        case 'image/png':
                              fileExtension = 'png';
                              break;
                        case 'image/gif':
                              fileExtension = 'gif';
                              break;
                        case 'image/webp':
                              fileExtension = 'webp';
                              break;
                        case 'image/heic':
                        case 'image/heif':
                              fileExtension = 'heic';
                              break;
                        case 'image/bmp':
                              fileExtension = 'bmp';
                              break;
                        case 'image/tiff':
                        case 'image/tif':
                              fileExtension = 'tiff';
                              break;
                        case 'image/svg+xml':
                              fileExtension = 'svg';
                              break;
                        default:
                              const nameExtension = file.name.split('.').pop().toLowerCase();
                              fileExtension = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp', 'tiff', 'tif', 'svg'].includes(nameExtension)
                                    ? nameExtension
                                    : 'jpg';
                  }

                  const photoIndex = photos.length;
                  s3Key = photoDataOverride?.s3Key || generateStructuredS3Key(photoIndex, fileExtension);

                  // Request presigned URL from API
                  const requestPayload = {
                        requestType: "getPresignedUrl",
                        s3Key: s3Key,
                        contentType: file.type,
                        fileSize: file.size,
                        referenceNo: referenceNo,
                        sectionName: sectionName,
                        itemIndex: itemIndex,
                        photoIndex: photoIndex + 1,
                        fileName: file.name,
                        fileExtension: fileExtension,
                        uploadTimestamp: new Date().toISOString(),
                        structuredNaming: true
                  };

                  const apiResponse = await fetch('https://r3vefztcpc.execute-api.ap-southeast-2.amazonaws.com/default/store-efs-report-photos-in-s3', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(requestPayload)
                  });

                  if (!apiResponse.ok) {
                        const errText = await apiResponse.text();
                        throw new Error(`Failed to get presigned URL: ${apiResponse.status} ${apiResponse.statusText} - ${errText}`);
                  }

                  const result = await apiResponse.json();

                  if (!result.presignedUrl) {
                        throw new Error(result.error || 'Presigned URL not returned by API');
                  }

                  // Upload directly to S3 using the presigned URL
                  const s3Response = await fetch(result.presignedUrl, {
                        method: 'PUT',
                        headers: { 'Content-Type': file.type },
                        body: file,
                        mode: 'cors'
                  });

                  if (!s3Response.ok) {
                        const text = await s3Response.text();
                        throw new Error(`S3 PUT failed: ${s3Response.status} ${s3Response.statusText} - ${text}`);
                  }

                  // Treat API result as success; the API also returns fileUrl/etag when available
                  const newPhoto = {
                        id: Date.now(),
                        filename: file.name,
                        s3Key: s3Key,
                        s3Url: result.fileUrl || `https://ef-event-shift-report-photostore.s3.amazonaws.com/${s3Key}`,
                        previewUrl: URL.createObjectURL(file),
                        uploadedAt: new Date().toISOString(),
                        fileSize: file.size,
                        fileType: file.type,
                        fileExtension: fileExtension,
                        etag: result.etag,
                        bucket: 'ef-event-shift-report-photostore',
                        sectionName: sectionName,
                        referenceNo: referenceNo,
                        itemIndex: itemIndex,
                        photoIndex: photoIndex + 1,
                        uploadStatus: 'success',
                        uploadMethod: 'presigned',
                        apiResponse: result,
                        structuredKey: true
                  };

                  if (!isRetry) {
                        const updatedPhotos = [...photos, newPhoto];
                        onPhotosChange(updatedPhotos);
                        saveToCache(updatedPhotos);
                  }

                  console.log(`Presigned upload completed successfully: ${file.name}`);
                  return true;
            } catch (error) {
                  console.error('Upload error:', error);

                  if (!isRetry) {
                        addToRetryQueue(file, photoDataOverride);

                        const failedPhoto = {
                              id: Date.now(),
                              filename: file.name,
                              s3Key: s3Key || generateStructuredS3Key(photos.length, file.name.split('.').pop()),
                              s3Url: null,
                              previewUrl: URL.createObjectURL(file),
                              uploadedAt: new Date().toISOString(),
                              fileSize: file.size,
                              fileType: file.type,
                              fileExtension: file.name.split('.').pop(),
                              bucket: 'ef-event-shift-report-photostore',
                              sectionName: sectionName,
                              referenceNo: referenceNo,
                              itemIndex: itemIndex,
                              photoIndex: photos.length + 1,
                              uploadStatus: 'failed',
                              error: error.message,
                              structuredKey: true
                        };

                        const updatedPhotos = [...photos, failedPhoto];
                        onPhotosChange(updatedPhotos);
                        saveToCache(updatedPhotos);
                  }

                  return false;
            }
      };

      // Base64 upload fallback was removed; ensure no stray return remains.

      // Multipart upload for larger files (requires API support)
      const uploadWithMultipart = async (file, s3Key, isRetry) => {
            // First, get presigned URL for direct S3 upload
            const presignedRequest = {
                  requestType: "getPresignedUploadUrl",
                  s3Key: s3Key,
                  fileType: file.type,
                  fileSize: file.size,
                  referenceNo: referenceNo,
                  sectionName: sectionName
            };

            const presignedResponse = await fetch('https://r3vefztcpc.execute-api.ap-southeast-2.amazonaws.com/default/store-efs-report-photos-in-s3', {
                  method: 'POST',
                  headers: {
                        'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(presignedRequest)
            });

            if (!presignedResponse.ok) {
                  throw new Error('Failed to get presigned URL, falling back to base64 upload');
            }

            const presignedData = await presignedResponse.json();

            // Upload directly to S3 using presigned URL
            const s3Response = await fetch(presignedData.uploadUrl, {
                  method: 'PUT',
                  headers: {
                        'Content-Type': file.type,
                  },
                  body: file
            });

            if (!s3Response.ok) {
                  throw new Error(`S3 upload failed: ${s3Response.status} ${s3Response.statusText}`);
            }

            // Notify API that upload is complete
            const notifyRequest = {
                  requestType: "confirmUpload",
                  s3Key: s3Key,
                  referenceNo: referenceNo,
                  sectionName: sectionName,
                  fileName: file.name,
                  fileSize: file.size,
                  fileType: file.type
            };

            const notifyResponse = await fetch('https://r3vefztcpc.execute-api.ap-southeast-2.amazonaws.com/default/store-efs-report-photos-in-s3', {
                  method: 'POST',
                  headers: {
                        'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(notifyRequest)
            });

            return await handleUploadResponse(notifyResponse, file, s3Key, isRetry);
      };

      // Handle upload response
      const handleUploadResponse = async (response, file, s3Key, isRetry) => {
            if (!response.ok) {
                  const errorText = await response.text();
                  throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
            }

            const result = await response.json();

            // Check for success
            const isSuccess = result.success === true ||
                  result.Success === true ||
                  response.status === 200;

            if (!isSuccess) {
                  throw new Error(result.error || result.Error || result.message || 'Upload failed');
            }

            // Add to photos array if not a retry
            if (!isRetry) {
                  const newPhoto = {
                        id: Date.now(),
                        filename: file.name,
                        s3Key: s3Key,
                        s3Url: result.data?.fileUrl || `https://ef-event-shift-report-photostore.s3.amazonaws.com/${s3Key}`,
                        uploadedAt: new Date().toISOString(),
                        fileSize: file.size,
                        fileType: file.type,
                        fileExtension: file.name.split('.').pop(),
                        etag: result.data?.etag,
                        bucket: 'ef-event-shift-report-photostore',
                        sectionName: sectionName,
                        referenceNo: referenceNo,
                        itemIndex: itemIndex,
                        photoIndex: photos.length + 1,
                        uploadStatus: 'success',
                        apiResponse: result,
                        structuredKey: true
                  };

                  const updatedPhotos = [...photos, newPhoto];
                  onPhotosChange(updatedPhotos);
                  saveToCache(updatedPhotos);
            }

            return true;
      };

      // Handle file selection - no artificial size limits
      const handleFileSelect = async (event) => {
            const files = Array.from(event.target.files);
            if (files.length === 0) return;

            if (photos.length + files.length > maxPhotos) {
                  setUploadError(`Cannot upload ${files.length} files. Maximum ${maxPhotos} photos allowed per section.`);
                  return;
            }

            setUploading(true);
            setUploadError(null);

            const errors = [];
            const maxFileSize = 50 * 1024 * 1024; // 50MB reasonable limit for mobile photos

            for (let i = 0; i < files.length; i++) {
                  const file = files[i];

                  if (!file.type.startsWith('image/')) {
                        errors.push(`${file.name}: Only image files are allowed`);
                        continue;
                  }

                  if (file.size > maxFileSize) {
                        errors.push(`${file.name}: File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max size: ${(maxFileSize / 1024 / 1024).toFixed(0)}MB`);
                        continue;
                  }

                  await uploadSinglePhoto(file);
            }

            if (errors.length > 0) {
                  setUploadError(errors.join('\n'));
            }

            setUploading(false);

            if (fileInputRef.current) {
                  fileInputRef.current.value = '';
            }
      };

      // Handle photo removal
      const handleRemovePhoto = (photoId) => {
            const updatedPhotos = photos.filter(photo => photo.id !== photoId);
            onPhotosChange(updatedPhotos);
            saveToCache(updatedPhotos);
      };

      // Handle browse button click
      const handleBrowseClick = () => {
            if (fileInputRef.current) {
                  fileInputRef.current.click();
            }
      };

      const failedUploads = retryQueue.filter(item => item.retryCount < item.maxRetries);

      return (
            <div style={{ marginTop: '1rem' }}>
                  <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '0.75rem'
                  }}>
                        <label style={{
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              color: '#374151'
                        }}>
                              Photos ({photos.length}/{maxPhotos}) - Item {itemIndex}
                              {retryQueue.length > 0 && (
                                    <span style={{ color: '#f59e0b', marginLeft: '0.5rem' }}>
                                          ({failedUploads.length} pending retry)
                                    </span>
                              )}
                        </label>

                        <button
                              type="button"
                              onClick={handleBrowseClick}
                              disabled={disabled || uploading || photos.length >= maxPhotos}
                              style={{
                                    background: disabled || uploading || photos.length >= maxPhotos ? '#9ca3af' : '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    cursor: disabled || uploading || photos.length >= maxPhotos ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease'
                              }}
                        >
                              {uploading ? 'Uploading...' : '📷 Add Photos'}
                        </button>
                  </div>

                  {/* Hidden file input */}
                  <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                  />

                  {/* Auto-retry status */}
                  {autoRetrying && (
                        <div style={{
                              background: '#fffbeb',
                              border: '1px solid #fbbf24',
                              borderRadius: '0.5rem',
                              padding: '1rem',
                              marginBottom: '1rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem'
                        }}>
                              <div style={{
                                    width: '1rem',
                                    height: '1rem',
                                    border: '2px solid #fbbf24',
                                    borderTop: '2px solid transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                              }}></div>
                              <span style={{ fontSize: '0.875rem', color: '#92400e', fontWeight: '500' }}>
                                    Retrying failed uploads...
                              </span>
                        </div>
                  )}

                  {/* Failed uploads retry section */}
                  {failedUploads.length > 0 && (
                        <div style={{
                              background: '#fef2f2',
                              border: '1px solid #fecaca',
                              borderRadius: '0.5rem',
                              padding: '1rem',
                              marginBottom: '1rem'
                        }}>
                              <div style={{
                                    fontSize: '0.875rem',
                                    color: '#dc2626',
                                    fontWeight: '600',
                                    marginBottom: '0.75rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                              }}>
                                    ⚠️ {failedUploads.length} photo{failedUploads.length > 1 ? 's' : ''} failed to upload
                              </div>
                              {failedUploads.map(item => (
                                    <div key={item.id} style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          background: '#fff',
                                          padding: '0.75rem',
                                          borderRadius: '0.375rem',
                                          marginBottom: '0.5rem',
                                          border: '1px solid #fecaca'
                                    }}>
                                          <div>
                                                <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>
                                                      {item.fileName}
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                      Attempting retry {item.retryCount + 1} of 3
                                                </div>
                                          </div>
                                          <button
                                                onClick={() => manualRetry(item.id)}
                                                style={{
                                                      background: '#dc2626',
                                                      color: 'white',
                                                      border: 'none',
                                                      padding: '0.5rem 1rem',
                                                      borderRadius: '0.375rem',
                                                      fontSize: '0.875rem',
                                                      fontWeight: '500',
                                                      cursor: 'pointer'
                                                }}
                                          >
                                                Retry Now
                                          </button>
                                    </div>
                              ))}
                        </div>
                  )}

                  {/* Upload error display */}
                  {uploadError && (
                        <div style={{
                              background: '#fee2e2',
                              border: '1px solid #fecaca',
                              borderRadius: '0.375rem',
                              padding: '0.75rem',
                              marginBottom: '0.75rem'
                        }}>
                              <div style={{ fontSize: '0.875rem', color: '#dc2626', fontWeight: '500' }}>
                                    Upload Error:
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#991b1b', whiteSpace: 'pre-line' }}>
                                    {uploadError}
                              </div>
                        </div>
                  )}

                  {/* Photos grid */}
                  {photos.length > 0 && (
                        <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                              gap: '1rem',
                              marginTop: '1rem'
                        }}>
                              {photos.map((photo) => (
                                    <div
                                          key={photo.id}
                                          style={{
                                                position: 'relative',
                                                border: `2px solid ${photo.uploadStatus === 'failed' ? '#ef4444' : '#e5e7eb'}`,
                                                borderRadius: '0.75rem',
                                                padding: '0.75rem',
                                                background: photo.uploadStatus === 'failed' ? '#fef2f2' : '#ffffff',
                                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                                          }}
                                    >
                                          {/* Photo preview */}
                                          <div style={{
                                                width: '100%',
                                                height: '120px',
                                                background: '#f3f4f6',
                                                borderRadius: '0.375rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginBottom: '0.75rem',
                                                position: 'relative',
                                                overflow: 'hidden'
                                          }}>
                                                {(photo.previewUrl || photo.s3Url) && photo.uploadStatus === 'success' ? (
                                                      <img
                                                            src={photo.previewUrl || photo.s3Url}
                                                            alt={photo.filename}
                                                            style={{
                                                                  width: '100%',
                                                                  height: '100%',
                                                                  objectFit: 'cover'
                                                            }}
                                                            onError={(e) => {
                                                                  // If preview fails, try S3 URL
                                                                  if (photo.previewUrl && e.target.src === photo.previewUrl && photo.s3Url) {
                                                                        e.target.src = photo.s3Url;
                                                                  } else {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextSibling.style.display = 'flex';
                                                                  }
                                                            }}
                                                      />
                                                ) : photo.uploadStatus === 'failed' && photo.previewUrl ? (
                                                      // For failed uploads, show local preview
                                                      <img
                                                            src={photo.previewUrl}
                                                            alt={photo.filename}
                                                            style={{
                                                                  width: '100%',
                                                                  height: '100%',
                                                                  objectFit: 'cover',
                                                                  opacity: 0.6
                                                            }}
                                                            onError={(e) => {
                                                                  e.target.style.display = 'none';
                                                                  e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                      />
                                                ) : null}

                                                <div style={{
                                                      width: '100%',
                                                      height: '100%',
                                                      display: (photo.previewUrl || photo.s3Url) ? 'none' : 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      flexDirection: 'column',
                                                      color: '#9ca3af'
                                                }}>
                                                      <span style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📷</span>
                                                      <span style={{ fontSize: '0.75rem' }}>Loading...</span>
                                                </div>

                                                {/* Status overlay */}
                                                {photo.uploadStatus === 'failed' && (
                                                      <div style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            background: 'rgba(239, 68, 68, 0.1)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            borderRadius: '0.375rem'
                                                      }}>
                                                            <div style={{
                                                                  background: '#dc2626',
                                                                  color: 'white',
                                                                  padding: '0.5rem 1rem',
                                                                  borderRadius: '0.375rem',
                                                                  fontSize: '0.875rem',
                                                                  fontWeight: '500'
                                                            }}>
                                                                  Upload Failed
                                                            </div>
                                                      </div>
                                                )}

                                                {/* Remove button */}
                                                <button
                                                      type="button"
                                                      onClick={() => handleRemovePhoto(photo.id)}
                                                      style={{
                                                            position: 'absolute',
                                                            top: '0.5rem',
                                                            right: '0.5rem',
                                                            background: 'rgba(0, 0, 0, 0.7)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '50%',
                                                            width: '2rem',
                                                            height: '2rem',
                                                            fontSize: '1.25rem',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'background 0.2s'
                                                      }}
                                                      onMouseOver={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.9)'}
                                                      onMouseOut={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.7)'}
                                                      title="Remove photo"
                                                >
                                                      ×
                                                </button>
                                          </div>

                                          {/* Photo info */}
                                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                <div style={{
                                                      fontWeight: '500',
                                                      marginBottom: '0.25rem',
                                                      wordBreak: 'break-word',
                                                      color: '#111827'
                                                }}>
                                                      {photo.filename}
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                      <span>{(photo.fileSize / 1024).toFixed(1)} KB</span>
                                                      {photo.uploadStatus === 'success' && (
                                                            <span style={{ color: '#10b981', fontSize: '0.75rem', fontWeight: '500' }}>
                                                                  ✓ Uploaded
                                                            </span>
                                                      )}
                                                </div>
                                          </div>
                                    </div>
                              ))}
                        </div>
                  )}

                  {/* Upload instructions */}
                  <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginTop: '0.5rem'
                  }}>
                        Add up to {maxPhotos} photos. Supported formats: JPG, PNG, GIF, WebP, HEIC. Max 50MB per photo.
                        {failedUploads.length > 0 && (
                              <div style={{ color: '#dc2626', marginTop: '0.25rem' }}>
                                    Some photos failed to upload - they'll retry automatically
                              </div>
                        )}
                  </div>
            </div>
      );
};

export default PhotoUpload;